import React from "react";
import DecisionPenLogo from "../../components/DecisionPenLogo";
import { v2cx } from "../theme/tokens";
import { useV2Theme } from "../theme/ThemeProvider";
import { useV2EnrichmentReview } from "../state/V2EnrichmentReviewContext";
import { V2_ROUTES } from "../state/useV2Navigation";
import {
  NAV_ITEMS,
  SIDEBAR_UTILITY_KEYS,
  IconForRoute,
  inboxBellTitle,
  approvalsBellTitle,
  formatNavBadgeCount,
} from "../nav/navItems";

const V2SidebarNav = ({ route, routes, onNavigate, isNavAllowed }) => {
  const { tokens } = useV2Theme();
  const {
    pendingReviewCount,
    inboxNotificationCount,
    hasInboxNotifications,
    baselineApprovalNotificationCount,
    hasBaselineApprovalNotifications,
  } = useV2EnrichmentReview();

  const allowed =
    typeof isNavAllowed === "function" ? isNavAllowed : () => true;

  const visibleItems = NAV_ITEMS.filter((item) => routes.includes(item.key));
  // Core loop (Ask, Add knowledge, Inbox, Approvals, Your map, Reports) on
  // top; utilities (Admin, Help) pinned to the bottom, icon-only.
  // Personal Settings lives in the top panel next to Sign out instead.
  const primaryItems = visibleItems.filter(
    (item) =>
      !SIDEBAR_UTILITY_KEYS.includes(item.key) &&
      // Personal Settings is not a sidebar item at all — it lives in the
      // top panel next to Sign out (excluding it from utilities alone
      // would demote it into this primary list).
      item.key !== V2_ROUTES.settings
  );
  const utilityItems = SIDEBAR_UTILITY_KEYS.map((key) =>
    visibleItems.find((item) => item.key === key)
  ).filter(Boolean);

  const renderItem = (item, { iconOnly = false } = {}) => {
    const active = route === item.key;
    const locked = !allowed(item.key);
    const showInboxBell =
      item.key === V2_ROUTES.insightsInbox && hasInboxNotifications;
    const showApprovalsBell =
      item.key === V2_ROUTES.baselineApprovals &&
      hasBaselineApprovalNotifications;
    const showNavBadge = showInboxBell || showApprovalsBell;
    const navBadgeCount = showInboxBell
      ? inboxNotificationCount
      : baselineApprovalNotificationCount;
    const navBadgeText = formatNavBadgeCount(navBadgeCount);
    const bellTitle = showInboxBell
      ? inboxBellTitle({ pendingReviewCount, inboxNotificationCount })
      : showApprovalsBell
        ? approvalsBellTitle(baselineApprovalNotificationCount)
        : item.title || item.label;
    const lockTitle = locked
      ? "Guided demo — finish this step before opening other areas"
      : showNavBadge
        ? bellTitle
        : item.title || item.label;
    return (
      <button
        key={item.key}
        type="button"
        title={lockTitle}
        aria-label={
          locked
            ? `${item.label} (locked during guided demo)`
            : showNavBadge
              ? `${item.label}, ${navBadgeCount} pending item${navBadgeCount === 1 ? "" : "s"}`
              : item.label
        }
        aria-disabled={locked || undefined}
        onClick={() => onNavigate(item.key)}
        className={v2cx(
          tokens.sidebar.itemBase,
          active ? tokens.sidebar.itemActive : tokens.sidebar.itemIdle,
          "relative",
          iconOnly && "!py-2",
          locked && !active && "cursor-not-allowed opacity-40"
        )}
      >
        <IconForRoute routeKey={item.key} active={active} tokens={tokens} />
        {showNavBadge ? (
          <span
            className="absolute -top-0.5 -right-0.5 flex min-h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold leading-none text-white shadow-md tabular-nums"
            aria-hidden
          >
            {navBadgeText}
          </span>
        ) : null}
        {iconOnly ? null : (
          <span
            className={
              active ? tokens.sidebar.labelActive : tokens.sidebar.labelIdle
            }
          >
            {item.label}
          </span>
        )}
      </button>
    );
  };

  return (
    <aside className={tokens.sidebar.root}>
      <div className="flex h-full w-full flex-col items-center gap-4 overflow-y-auto py-4 overscroll-contain">
        <div className={tokens.sidebar.logoWrap}>
          <DecisionPenLogo size={24} />
        </div>
        <nav
          className="flex w-full flex-col items-center gap-2 px-2"
          aria-label="Primary"
        >
          {primaryItems.map((item) => renderItem(item))}
        </nav>
        {utilityItems.length ? (
          <div className="mt-auto w-full px-2">
            <nav
              className="flex w-full flex-col items-center gap-1 border-t border-slate-200 pt-2"
              aria-label="Utilities"
            >
              {utilityItems.map((item) => renderItem(item, { iconOnly: true }))}
            </nav>
          </div>
        ) : null}
      </div>
    </aside>
  );
};

export default V2SidebarNav;
